import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { PALIERS, type Palier } from '@artosera/shared';
import { Badge, Button, Select, useToast } from '@artosera/ui';
import { modifierGalerie } from '../../lib/api.js';
import { usePeut } from '../../auth/usePeut.js';
import { formaterDate, formaterOctets } from '../../lib/format.js';
import type { GalerieFiche } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import formStyles from '../../styles/form.module.css';

export function IdentiteTab({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const peutModifier = peut('console:provision-gallery');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [palier, setPalier] = useState<Palier>(galerie.palier);

  // Le palier ne règle plus que les quotas (D21) : changer de palier ici ne
  // touche pas à la liste des modules. Les modules — et l'application d'un
  // palier comme préréglage — se règlent dans l'onglet Modules.
  const mutation = useMutation({
    mutationFn: () => modifierGalerie(idGalerie, { palier }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
      await queryClient.invalidateQueries({ queryKey: ['console', 'galeries'] });
      toast({ title: t('fiche.identite.succes'), closeLabel: t('fiche.domaines.fermer') });
    },
  });

  return (
    <div className={pageStyles.panneau}>
      <div className={pageStyles.grilleStats}>
        <dl className={pageStyles.stat}>
          <dt>{t('fiche.identite.champEtat')}</dt>
          <dd>
            <Badge
              tone={
                galerie.etat === 'active'
                  ? 'available'
                  : galerie.etat === 'suspendue'
                    ? 'reserved'
                    : 'sold'
              }
            >
              {t(`galeries.etat.${galerie.etat}`)}
            </Badge>
          </dd>
        </dl>
        <dl className={pageStyles.stat}>
          <dt>{t('fiche.identite.groupe')}</dt>
          <dd>{galerie.groupe ?? t('fiche.identite.aucunGroupe')}</dd>
        </dl>
        <dl className={pageStyles.stat}>
          <dt>{t('fiche.identite.stockage')}</dt>
          <dd className={galerie.stockage === null ? pageStyles.mesureAbsente : undefined}>
            {/*
             * `stockage` est `null` tant que le travail nocturne
             * `storage:measure` n'est pas passé (docs/dev/api.md § 12).
             * Afficher « 0 o / … » laisserait croire à une mesure réelle : on
             * dit alors honnêtement qu'elle n'a pas encore eu lieu.
             */}
            {galerie.stockage === null
              ? t('fiche.identite.stockageIndisponible', {
                  quota: formaterOctets(galerie.quotas.stockageGo * 1024 ** 3),
                })
              : t('fiche.identite.stockageMesure', {
                  utilise: formaterOctets(galerie.stockage.octets),
                  quota: formaterOctets(galerie.quotas.stockageGo * 1024 ** 3),
                  objets: galerie.stockage.objets,
                  mesureLe: formaterDate(galerie.stockage.mesureLe, ''),
                })}
          </dd>
        </dl>
        <dl className={pageStyles.stat}>
          <dt>{t('fiche.identite.membresMax')}</dt>
          <dd>
            {galerie.quotas.membresMax === null
              ? t('fiche.identite.illimite')
              : galerie.quotas.membresMax}
          </dd>
        </dl>
      </div>

      <div className={formStyles.grille}>
        <div className={formStyles.champ}>
          <label htmlFor="identite-palier">{t('fiche.identite.champPalier')}</label>
          <Select
            id="identite-palier"
            disabled={!peutModifier}
            value={palier}
            onValueChange={(valeur) => setPalier(valeur as Palier)}
            aria-describedby="identite-palier-aide"
            options={PALIERS.map((valeur) => ({
              value: valeur,
              label: t(`galeries.palier.${valeur}`),
            }))}
          />
          <p id="identite-palier-aide" className={formStyles.aide}>
            {t('fiche.identite.palierAide')}{' '}
            <Link to={`/galeries/${idGalerie}/modules`}>{t('fiche.identite.lienModules')}</Link>
          </p>
        </div>
      </div>

      {peutModifier ? (
        <div className={formStyles.actionsFormulaire}>
          <Button loading={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? t('fiche.identite.enCours') : t('fiche.identite.enregistrer')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
